type CCTVRequest = {
    id: string;
    requestId?: string;
    poNumber?: string;
    dateOfRequest?: string;
    branchCode?: string;
    branchName?: string;
    branchId?: string;
    vendor?: string;
    vendorId?: string;
    items?: Array<{
        itemId: string;
        itemCode: string;
        itemName: string;
        itemDescription?: string;
        quantity: number;
    }>;
    remarks?: string;
    status?: string;
    [key: string]: any;
};

type Branch = {
    id: string;
    branchCode?: string;
    address?: string;
    name?: string;
    [key: string]: any;
};

type Vendor = {
    contactPersonName?: string;
    companyName?: string;
    companyAddress?: string;
    [key: string]: any;
};

type InventoryItem = {
    id: string;
    itemCode?: string;
    itemName?: string;
    itemDescription?: string;
    brandName?: string;
    makeModel?: string;
    rates?: number;
    unitPrice?: number;
    [key: string]: any;
};

type InchargeInfo = {
    name?: string;
    email?: string;
};

// Generate Purchase Order Number (Fallback logic)
export const generatePurchaseOrderNumber = (branchCode: string = 'UNKNOWN'): string => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const basePo = `PO${branchCode}${dd}${mm}`;
    const randomSuffix = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `${basePo}-${randomSuffix}`;
};

// Format date as DD-MM-YYYY
const formatDateDDMMYYYY = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

// Calculate delivery date (PO date + 7 days)
const calculateDeliveryDate = (poDate: Date): string => {
    const deliveryDate = new Date(poDate);
    deliveryDate.setDate(deliveryDate.getDate() + 7);
    return formatDateDDMMYYYY(deliveryDate);
};

export const formatCCTVEmailBody = (
    request: CCTVRequest,
    vendor: Vendor | null,
    branches: Branch[] = [],
    bankData?: any
): string => {
    // Try to get bank name from bankData or branches data or default to HBL
    const bankName = bankData?.companyName || 'Bank';
    const branchName = request.branchName || 'N/A';
    const branchCode = request.branchCode || 'N/A';
    const branchId = request.branchId || '';

    // Find branch in branches array to get address
    const branch = branches.find(b => b.id === request.branchId || b.branchCode === request.branchCode);
    const branchAddress = branch?.address || request.branchName || 'N/A';

    const vendorName = vendor?.contactPersonName || vendor?.companyName || 'Sir/Madam';
    const vendorCompany = vendor?.companyName || 'Vendor Company';

    const itemsTable = request.items && request.items.length > 0
        ? request.items.map((item, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${item.itemName || 'N/A'}</td>
                <td>${item.quantity || 0}</td>
                <td>Make Model must as per approved SLA model</td>
                <td>Rates will be charged as per NEW approved SLA rates</td>
            </tr>
        `).join('')
        : '<tr><td colspan="5" style="text-align: center;">No items</td></tr>';

    const htmlBody = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>CCTV Hardware Request</title>
        <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; }
            .header { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); color: #333; padding: 20px; border-radius: 8px 8px 0 0; border: 2px solid #dee2e6; }
            .content { background: #ffffff; padding: 20px; border-radius: 0 0 8px 8px; border: 2px solid #dee2e6; border-top: none; }
            .greeting { margin: 20px 0; }
            .hardware-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .hardware-table th, .hardware-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .hardware-table th { background-color: #007bff; color: white; }
            .hardware-table td { background-color: #f8f9fa; }
            .instructions { background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #28a745; }
            .instructions ul { margin: 10px 0; padding-left: 20px; }
            .instructions li { margin: 5px 0; }
            .footer { margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; }
        </style>
    </head>
    <body>
        <div class="header">
            <h2>${bankName} - CCTV Hardware Request</h2>
            <p>PO Number: ${request.poNumber || request.requestId || request.id}</p>
            <p>Date: ${request.dateOfRequest ? new Date(request.dateOfRequest).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
        </div>
        
            <div class="content">
                <div class="greeting">
                <p>Dear ${vendorName} M/S ${vendorCompany},</p>
                </div>
            
            <p>As per approval of head Security Services and existing SLA with ${vendorCompany} please procure and install the below mentioned CCTV Hardware and cable at our <strong>${bankName} ${branchName} Branch ${branchCode}${branchId ? ' ' + branchId : ''}</strong>${branchAddress && branchAddress !== branchName ? ' (' + branchAddress + ')' : ''}.</p>
            
            <h3>Hardware Information</h3>
            <table class="hardware-table">
                <thead>
                    <tr>
                        <th>S. No.</th>
                        <th>Hardware Information</th>
                        <th>Quantity</th>
                        <th>Make/Model</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsTable}
                </tbody>
            </table>
            
            <div class="instructions">
                <h4>Vendor Team Instructions</h4>
                <p>Please ensure the following points need to be accomplished after the installation of said hardware.</p>
                <ul>
                    <li>Complete and proper <strong>Delivery Challan (Make / Quantity / Serial No.)</strong> should be properly filled and <strong>signed from relevant vendor and branch staff.</strong></li>
                    <li>Please confirm when installation has done.</li>
                </ul>
                <p><strong>Payment Terms:</strong> Above all; please note that payment will be made as per the above mentioned hardware only; if any additional hardware would be required then PSO will take separate approval of these items from concerned management prior to invoicing.</p>
            </div>
            
                <div class="footer">
                    <p>Best regards,</p>
                    <p><strong>PSO Team</strong></p>
            </div>
        </div>
    </body>
    </html>
    `;

    return htmlBody;
};

export const formatCCTVPurchaseOrderEmail = (
    request: CCTVRequest,
    vendor: Vendor | null,
    branches: Branch[] = [],
    inchargeInfo: InchargeInfo = {},
    poNumber?: string,
    poDate?: Date,
    inventoryItems: InventoryItem[] = [],
    branchPsoName?: string,
    branchPsoRegion?: string,
    taxPercentage: number = 18,
    bankData?: any
): string => {
    console.log("DEBUG: Full Bank Data:", bankData);
    const bankName = bankData?.companyName || 'Bank';
    const branchName = request.branchName || 'N/A';
    const branchCode = request.branchCode || 'N/A';

    const logoUrl = bankData?.imgUrl || "";
    console.log("DEBUG: PO Bank Image URL:", logoUrl);
    const branch = branches.find(b => b.id === request.branchId || b.branchCode === request.branchCode);
    const branchAddress = branch?.address || request.branchName || 'N/A';

    // Vendor information
    const vendorName = vendor?.contactPersonName || 'Sir/Madam';
    const vendorCompany = request.vendor || vendor?.companyName || 'Vendor Company';
    const vendorAddress = vendor?.companyAddress || vendor?.address || 'N/A';
    const vendorContactPerson = vendor?.contactPersonName || 'N/A';

    // Incharge information
    const buyerName = inchargeInfo.name || 'Incharge';
    const buyerEmail = inchargeInfo.email || 'N/A';

    // Purchase Order details
    const purchaseOrderNumber = poNumber || request.poNumber || generatePurchaseOrderNumber(branchCode);
    const purchaseOrderDate = poDate || new Date();
    const poDateFormatted = formatDateDDMMYYYY(purchaseOrderDate);
    const deliveryDate = calculateDeliveryDate(purchaseOrderDate);

    // Calculate total amount (fetch rates from inventory items if not in request items)
    // Note: rates already includes tax, so we calculate backwards
    let totalBaseAmount = 0;
    let totalTaxAmount = 0;
    let totalAmountWithTax = 0;

    const itemsTable = request.items && request.items.length > 0
        ? request.items.map((item: any, index) => {
            // Try to get rates from item first, then from inventory
            let unitPriceWithTax = item.rates || item.unitPrice || 0;
            let makeModel = item.makeModel || item.brandName || 'As per SLA';
            let itemTaxPercentage = taxPercentage; // Default to global tax percentage

            // If rates not in item, fetch from inventory
            if (!unitPriceWithTax && item.itemId) {
                const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                if (inventoryItem) {
                    unitPriceWithTax = inventoryItem.rates || inventoryItem.unitPrice || 0;
                    makeModel = inventoryItem.makeModel || inventoryItem.brandName || makeModel;
                    // Use tax percentage from inventory item if available
                    itemTaxPercentage = inventoryItem.taxPercentage ?? taxPercentage;
                }
            } else if (item.itemId) {
                // Get tax percentage from inventory item even if we have rates
                const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                if (inventoryItem && inventoryItem.taxPercentage !== undefined) {
                    itemTaxPercentage = inventoryItem.taxPercentage;
                }
            }

            const quantity = item.quantity || 0;
            // unitPriceWithTax already includes tax, so calculate backwards
            const taxRate = itemTaxPercentage / 100;
            const totalPriceWithTax = unitPriceWithTax * quantity;
            // Base Price = Price with Tax / (1 + tax rate)
            const basePrice = totalPriceWithTax / (1 + taxRate);
            // Unit Base Price = Base Price / quantity
            const unitBasePrice = quantity > 0 ? basePrice / quantity : 0;
            // Tax = Price with Tax - Base Price
            const tax = totalPriceWithTax - basePrice;

            totalBaseAmount += basePrice;
            totalTaxAmount += tax;
            totalAmountWithTax += totalPriceWithTax;

            // Get item description from item or inventory
            let itemDescription = item.itemDescription || '';
            if (!itemDescription && item.itemId) {
                const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                if (inventoryItem) {
                    itemDescription = inventoryItem.itemDescription || '';
                }
            }

            // Determine UOM (Unit of Measure)
            const lowerItemName = (item.itemName || '').toLowerCase();
            const lowerDescription = (itemDescription || '').toLowerCase();
            const uom = (lowerItemName.includes('cabling') || lowerItemName.includes('piping') ||
                lowerDescription.includes('cabling') || lowerDescription.includes('piping')) ? 'RFT' : 'Pcs';

            return `
                <tr>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${index + 1}</td>
                    <td style="border: 1px solid #000000; padding: 8px; font-size: 11px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; max-width: 120px;">${itemDescription || item.itemName || 'N/A'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${uom}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${quantity}</td>
                    <td style="border: 1px solid #000000; padding: 8px; font-size: 11px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; max-width: 100px;">${makeModel}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: right; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `Rs. ${unitBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: right; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `Rs. ${basePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}</td>
                </tr>
            `;
        }).join('')
        : '<tr><td colspan="7" style="text-align: center; border: 1px solid #ddd; padding: 8px;">No items</td></tr>';

    // Grand total = total amount with tax
    const grandTotal = totalAmountWithTax;

    // Format contact person name - handle cases where name might have "Mr." prefix
    let formattedContactName = vendorName;
    if (formattedContactName && !formattedContactName.toLowerCase().startsWith('mr.')) {
        formattedContactName = `Mr. ${formattedContactName}`;
    }

    // Format PSO name - handle cases where name might have "Mr." prefix
    let formattedPsoName = branchPsoName || 'PSO';
    if (formattedPsoName && !formattedPsoName.toLowerCase().startsWith('mr.')) {
        formattedPsoName = `Mr. ${formattedPsoName}`;
    }

    const htmlBody = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>CCTV Purchase Order</title>
        <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.5; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
            * { color: #000000 !important; }
            .outer-wrapper { padding: 0; margin: 0; width: 100%; }
            .container { width: 100%; max-width: 100%; margin: 0; padding: 20px; box-sizing: border-box; }
            
            /* Letterhead */
            .letterhead { display: flex; align-items: center; border-bottom: 2px solid ${bankData?.brandColor || bankData?.color || '#333'}; padding-bottom: 10px; margin-bottom: 40px; }
            .bank-logo { height: 40px; margin-right: 15px; }
            .bank-info { flex-grow: 1; text-align: center; }
            .bank-info h2 { margin: 0; color: ${bankData?.brandColor || bankData?.color || '#333'} !important; font-size: 24px; font-weight: bold; }

            .report-title { text-align: center; margin-bottom: 0; }
            .report-title h1 { margin: 0; font-size: 16px; font-weight: bold; text-decoration: underline; color: #000 !important; }
            .branch-subtitle { font-size: 13px; margin-top: 3px; font-weight: bold; }

            .info-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; table-layout: fixed; }
            .info-table td { padding: 6px 10px; border: 1px solid #000; vertical-align: top; font-size: 11px; line-height: 1.3; }
            .label-cell { background-color: #f7f7f7; font-weight: bold; width: 22%; }
            .value-cell { width: 28%; word-break: break-word; white-space: pre-wrap; }
            .full-value { width: 78%; word-break: break-word; white-space: pre-wrap; }

            .items-section h3 { margin: 15px 0 8px 0; font-size: 14px; font-weight: bold; color: ${bankData?.brandColor || bankData?.color || '#333'} !important; }
            .items-table { width: 100%; border-collapse: collapse; table-layout: auto; }
            .items-table th { background-color: #f7f7f7; font-weight: bold; padding: 8px 6px; text-align: left; border: 1px solid #000; font-size: 10px; }
            .items-table td { padding: 8px 6px; border: 1px solid #000; font-size: 10px; vertical-align: top; }
            
            .totals-table { width: 100%; max-width: 300px; margin-left: auto; border-collapse: collapse; margin-top: 10px; }
            .totals-table td { padding: 6px 10px; border: 1px solid #000; font-size: 11px; }
            .totals-label { font-weight: bold; text-align: right; background-color: #f7f7f7; }
            .totals-value { text-align: right; }
            .grand-total-row { font-weight: bold; background-color: #eee; }

            .section-title { font-weight: bold; font-size: 12px; margin: 15px 0 8px 0; text-decoration: underline; color: ${bankData?.brandColor || bankData?.color || '#333'} !important; }
            .list-content { margin: 0; padding-left: 18px; font-size: 10.5px; line-height: 1.5; }
            .list-content li { margin-bottom: 3px; }

            .footer-content { margin-top: 20px; font-size: 11px; line-height: 1.5; }
            .signature-area { margin-top: 35px; border-top: 1px solid #000; width: 200px; padding-top: 5px; }
            .page-break { display: block !important; page-break-before: always !important; break-before: page !important; padding-top: 30px !important; clear: both !important; }
        </style>
    </head>
    <body>
        <div class="outer-wrapper">
            <div class="container">
                <div class="letterhead">
                    <img src="${logoUrl}" alt="${bankName} Logo" class="bank-logo" />
                    <div class="bank-info">
                        <div class="report-title">
                            <h1>PURCHASE ORDER</h1>
                            <div class="branch-subtitle">${branchCode} - ${branchName}</div>
                        </div>
                    </div>
                </div>

               
                
                <table class="info-table">
                    <tr>
                        <td class="label-cell">Purchase Order Number</td>
                        <td class="value-cell">${purchaseOrderNumber}</td>
                        <td class="label-cell">Purchase Order Date</td>
                        <td class="value-cell">${poDateFormatted}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Buyer Name</td>
                        <td class="value-cell">${buyerName}</td>
                        <td class="label-cell">Email ID</td>
                        <td class="value-cell">${buyerEmail}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Vendor Name</td>
                        <td class="value-cell">${vendorCompany}</td>
                        <td class="label-cell">Vendor Address</td>
                        <td class="value-cell">${vendorAddress}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Contact Person</td>
                        <td class="value-cell">${vendorContactPerson}</td>
                        <td class="label-cell">Delivery Date</td>
                        <td class="value-cell">${deliveryDate}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Amount</td>
                        <td colspan="3" class="full-value" style="font-weight: bold; font-size: 13px;">Rs. ${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                        <td class="label-cell">Payment Terms</td>
                        <td colspan="3" class="full-value">Net Within 30 days upon submission of required documents.</td>
                    </tr>
                </table>
                
                <!-- PDF_SPLIT_ITEMS -->
                <div class="items-section">
                    <h3>Item details:</h3>
                    <table class="items-table">
                        <thead>
                            <tr>
                                <th style="width: 40px; text-align: center;">S. NO</th>
                                <th>CCTV Equipment Name</th>
                                <th style="width: 40px; text-align: center;">UOM</th>
                                <th style="width: 40px; text-align: center;">Qty</th>
                                <th>Make/Model</th>
                                <th style="text-align: right;">Unit Price</th>
                                <th style="text-align: right;">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${itemsTable}
                        </tbody>
                    </table>
                </div>

                <table class="totals-table">
                    <tr>
                        <td class="totals-label">Sub Total (Price):</td>
                        <td class="totals-value">Rs. ${totalBaseAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                        <td class="totals-label">Sales tax:</td>
                        <td class="totals-value">Rs. ${totalTaxAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr class="grand-total-row">
                        <td class="totals-label">Grand Total (Price with Tax):</td>
                        <td class="totals-value">Rs. ${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                </table>

                <!-- PDF_SPLIT_TERMS -->
                <div class="page-break">
                    <div class="section-title">Terms and Conditions:</div>
                    <ol class="list-content">
                        <li>All the hardware must comply with specifications as per approved SLA</li>
                        <li>Delivery must be completed and intimated by date mentioned above</li>
                        <li>PO Number must be mentioned on all invoices, delivery challan and acknowledgement of returned hardware form (if applicable)</li>
                        <li>Any delay lead to penalties or cancellation of Purchase order</li>
                        <li>Complete and proper Delivery Challan (Make / Quantity / Serial No.) should be properly filled and signed from relevant vendor and branch staff.</li>
                        <li>In case of replacement of hardware acknowledgement of returned hardware form must be submitted with the invoices</li>
                    </ol>
    
                    <div class="section-title">Please Note:</div>
                    <ol class="list-content">
                        <li>Payment will be made as per the above mentioned hardware only; if any additional hardware would be required then PSO will take separate approval of these items from concerned management prior to invoicing</li>
                        <li>${bankName} reserves rights to cancel the subject Work Order if the installation is not completed within the stipulated time. The branch may be allocated to other vendor in case of non-compliance of SLA</li>
                    </ol>
                </div>

                ` + /*
                <div class="footer-content">
                    <p>Dear ${formattedContactName}, M/S ${vendorCompany}</p>
                    
                    <p>
                        In the light of existing SLA, please procure and install the above mentioned CCTV Hardware and cable at ${bankName} Branch Code ${branchCode} ${branchAddress} by ${deliveryDate}
                    </p>
                    
                    ${branchPsoName ? `
                    <p>
                        Dear ${formattedPsoName}${branchPsoRegion && branchPsoRegion !== 'Region' && !branchPsoRegion.toLowerCase().includes('pso') ? `, ${branchPsoRegion}` : ''}
                    </p>
                    
                    <p>
                        Please coordinate with branch and vendor for installation of all CCTV equipment as per SLA by M/S ${vendorCompany}. In case of requirement of any additional equipment, kindly get approval from Head Security for further work processing.
                    </p>
                    ` : ''}
                </div>

                <div class="signature-area">
                    <strong>${buyerName || bankName + ' Support'}</strong>
                </div>
                */ `
            </div>
        </div>
    </body>
    </html>
    `;


    return htmlBody;
};

